import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, HandHeart } from "lucide-react";
import { MOTTO_TRANSLATION } from "@/data/brothers";

const heroImages = [
  { src: "/assets/hero/hero-2.webp", alt: "Brothers sharing a meal together in Uganda", pos: "center 40%" },
  { src: "/assets/hero/hero-1.jpg", alt: "Joseph House residents gathered together as a community", pos: "center 30%" },
  { src: "/assets/hero/hero-4.jpg", alt: "The Josephian community outside a parish church", pos: "center 35%" },
  { src: "/assets/hero/hero-3.jpg", alt: "Aspiring brothers of the Josephian community in Uganda", pos: "center 35%" },
];

const fade = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] },
  }),
};

export default function HeroCarousel() {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setCurrent((c) => (c + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(id);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      <div className="absolute inset-0" data-testid="hero-photo-carousel">
        {heroImages.map((img, i) => (
          <div
            key={img.src}
            className={`absolute inset-0 transition-opacity duration-[2000ms] ease-in-out ${
              i === current ? "opacity-100" : "opacity-0"
            }`}
          >
            {/* Blurred backdrop for mobile portrait screens */}
            <img
              src={img.src}
              alt=""
              className="absolute inset-0 w-full h-full object-cover blur-md scale-110 opacity-60 md:hidden"
            />
            {/* Crisp portrait/landscape image */}
            <img
              src={img.src}
              alt={img.alt}
              onError={(e) => { e.currentTarget.style.display = "none"; }}
              style={{ objectPosition: img.pos }}
              className={`absolute inset-0 w-full h-full object-contain md:object-cover ${
                i === current ? "animate-kenburns" : ""
              }`}
            />
          </div>
        ))}
        <div className="absolute inset-0 bg-gradient-to-br from-[#2B1A10]/80 via-[#4A2E1C]/65 to-[#7A4A2E]/50" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#2B1A10]/85 via-transparent to-[#2B1A10]/20" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-24 w-full">
        <motion.p
          initial="hidden" animate="show" variants={fade} custom={0}
          className="text-brand-sand text-sm uppercase tracking-[0.3em] font-semibold mb-6"
          data-testid="hero-overline"
        >
          The Josephian Brothers of Jesus · {MOTTO_TRANSLATION}
        </motion.p>
        <motion.h1
          initial="hidden" animate="show" variants={fade} custom={1}
          className="font-serif font-light text-brand-bg text-4xl sm:text-6xl lg:text-7xl leading-[1.05] max-w-4xl"
          data-testid="hero-heading"
        >
          &quot;At the moment they are released from prison, they have a place and a people to whom they belong.&quot;
        </motion.h1>
        <motion.p
          initial="hidden" animate="show" variants={fade} custom={2}
          className="text-brand-bg/80 text-lg mt-8 max-w-2xl leading-relaxed"
        >
          A radical statement of belonging — offering shade, dignity, and hope to those rejected
          by the world or forgotten behind bars.
        </motion.p>
        <motion.div
          initial="hidden" animate="show" variants={fade} custom={3}
          className="flex flex-col sm:flex-row gap-4 mt-10"
        >
          <Link
            to="/support"
            data-testid="hero-support-btn"
            className="inline-flex items-center justify-center gap-2 bg-brand-sand text-brand-ink hover:bg-brand-bg px-8 py-4 rounded-full font-medium transition-all duration-300"
          >
            Get Involved <HandHeart className="w-4 h-4" />
          </Link>
          <Link
            to="/brothers"
            data-testid="hero-story-btn"
            className="inline-flex items-center justify-center gap-2 border border-brand-bg/40 text-brand-bg hover:bg-brand-bg/10 px-8 py-4 rounded-full font-medium transition-all duration-300"
          >
            Learn Our Story <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
